import { useQuery } from "@tanstack/react-query";
import { PROOFING_FLOW_APP_ID } from "@/config";
import {
  projectsBulkGetProjectsByIds,
  usersGetUserProjects,
} from "@/usersAndProjects-client/sdk.gen";
import type { ProjectWithPackages } from "../utils/packageEnrichment";

/**
 * Fetch user's proofing projects with their assigned packages
 *
 * This hook fetches only the projects assigned to the user (via /api/users/{sub}/projects)
 * filtered by the proofing app, then performs one bulk project fetch. This reduces
 * API calls by only fetching projects the user has access to, instead of all tenant projects.
 */
export interface UseFetchProjectsWithPackagesResult {
  projectsWithPackages: ProjectWithPackages[] | null;
  isLoading: boolean;
  error: Error | null;
}

export const useFetchProjectsWithPackages = (
  userSub: string | null | undefined,
  tenantSlug?: string,
  options?: { enabled?: boolean },
): UseFetchProjectsWithPackagesResult => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["projectsWithPackages", "user", userSub],
    queryFn: async () => {
      if (!userSub) {
        throw new Error("User sub is required");
      }

      // Step 1: Fetch user's projects filtered by proofing app (includes group projects)
      const projectsResponse = await usersGetUserProjects({
        path: { sub: userSub },
        query: {
          appId: PROOFING_FLOW_APP_ID,
          includeGroupProjects: "true",
        },
      });
      const projects = projectsResponse.data || [];
      if (projects.length === 0) {
        return [];
      }

      const projectIds = projects.map((project) => project.id);
      const bulkResponse = await projectsBulkGetProjectsByIds({
        body: {
          projectIds,
        },
      });
      const bulkProjects = bulkResponse.data || [];

      const projectsById = new Map(projects.map((project) => [project.id, project]));

      const projectsWithPackages: ProjectWithPackages[] = bulkProjects.map((bulkProject) => {
        const userProject = projectsById.get(bulkProject.id);

        return {
          ...bulkProject,
          tenantSlug: tenantSlug || bulkProject.tenantSlug || "",
          packages: bulkProject.packages || [],
          assignedPackages: [],
          assignedUsers: [],
          ...(userProject ? { ownedBy: userProject.ownedBy } : {}),
        };
      });

      return projectsWithPackages;
    },
    enabled: (options?.enabled ?? true) && !!userSub,
    staleTime: 10 * 60 * 1000, // Cache for 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    projectsWithPackages: data || null,
    isLoading,
    error: error as Error | null,
  };
};
